import type { Order, User, Menu } from '../types';
import { fromISO, formatDate } from './dates';

interface AdminRow {
  order: Order;
  user: User;
  recipientLastName: string;
  recipientFirstName: string;
  company: string;
  redeemed: boolean;
}

export function buildAdminRows(orders: Order[], users: User[]): AdminRow[] {
  const userMap = new Map(users.map((u) => [u.id, u]));

  return orders.map((order) => {
    const user = userMap.get(order.userId);
    let recipientLastName = '';
    let recipientFirstName = '';
    let company = user?.company || '';

    if (order.recipientType === 'self') {
      recipientLastName = user?.lastName || '';
      recipientFirstName = user?.firstName || '';
    } else if (order.recipientType === 'colleague') {
      const colleague = userMap.get(order.colleagueId || '');
      recipientLastName = colleague?.lastName || '';
      recipientFirstName = colleague?.firstName || '';
      company = colleague?.company || company;
    } else if (order.recipientType === 'guest') {
      const parts = (order.recipientName || '').split(' ');
      recipientFirstName = parts[0] || '';
      recipientLastName = parts.slice(1).join(' ') || parts[0] || '';
    }

    return {
      order,
      user: user!,
      recipientLastName,
      recipientFirstName,
      company,
      redeemed: order.redeemed,
    };
  });
}

export function exportToCSV(
  orders: Order[],
  users: User[],
  _menus: Menu[],
  redemptions: Set<string>
): void {
  const userMap = new Map(users.map((u) => [u.id, u]));

  const BOM = '﻿';
  const header = 'Datum;Nachname;Vorname;Unternehmen;Gericht;Abholcode;Code genutzt;Hinweis';

  const rows = orders.map((order) => {
    const user = userMap.get(order.userId);
    let recipientLastName = '';
    let recipientFirstName = '';
    let company = user?.company || '';
    let abholcode = '';

    if (order.recipientType === 'self') {
      recipientLastName = user?.lastName || '';
      recipientFirstName = user?.firstName || '';
      abholcode = user?.pickupCode || '';
    } else if (order.recipientType === 'colleague') {
      const colleague = userMap.get(order.colleagueId || '');
      recipientLastName = colleague?.lastName || '';
      recipientFirstName = colleague?.firstName || '';
      company = colleague?.company || company;
      abholcode = colleague?.pickupCode || '';
    } else if (order.recipientType === 'guest') {
      const parts = (order.recipientName || '').split(' ');
      recipientFirstName = parts[0] || '';
      recipientLastName = parts.slice(1).join(' ') || '';
      abholcode = order.guestCode || '';
    }

    const datum = formatDate(fromISO(order.dateISO));
    const gericht = order.dish.name;
    const codeGenutzt = redemptions.has(order.id) ? 'Ja' : 'Nein';
    let hinweis = '';
    if (order.cancelled) hinweis = 'Storniert';

    return [datum, recipientLastName, recipientFirstName, company, gericht, abholcode, codeGenutzt, hinweis]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(';');
  });

  const csv = BOM + header + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `foody-bestellungen-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
